import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEntityForm } from '@/shared/lib/useEntityForm';
import { AsyncMultiSelect } from '@/shared/ui/AsyncMultiSelect';
import { ErrorList } from '@/shared/ui/ErrorList';
import { FilterSelect } from '@/shared/ui/FilterSelect';
import { FormField } from '@/shared/ui/FormField';
import { ModalDialog } from '@/shared/ui/ModalDialog';
import type { SelectOption } from '@/shared/ui/selectTypes';
import { TextField } from '@/shared/ui/TextField';
import { COUNTRY_OPTIONS, NOTIFICATION_TYPE_OPTIONS } from './constants';
import {
  NOTIFICATION_TYPE,
  fetchRegionOptions,
  saveNotification,
  searchCarMarks,
  searchCities,
  searchUsers,
  type MobilePlatform,
  type NotificationValues,
} from './api';

const PLATFORMS: Array<{ value: MobilePlatform; label: string }> = [
  { value: 'ios', label: 'iOS' },
  { value: 'android', label: 'Android' },
];

const MAX_MESSAGE_LENGTH = 1000;
const FIELD_WIDTH = 300;

type Props = {
  /** Set when editing an existing notification. */
  id?: number;
  initial: NotificationValues;
  categories: SelectOption[];
  onClose: () => void;
};

export const NotificationModal = ({ id, initial, categories, onClose }: Props) => {
  const qc = useQueryClient();
  const { values, errors, setField, save, isSaving } = useEntityForm(
    initial,
    (v: NotificationValues) => saveNotification(id, v),
    () => {
      void qc.invalidateQueries({ queryKey: ['notifications'] });
      onClose();
    },
  );

  const regions = useQuery({
    queryKey: ['notification-regions', values.countryId],
    queryFn: () => fetchRegionOptions(values.countryId),
    enabled: Boolean(values.countryId),
  });

  const changeCountry = (countryId: string) => {
    setField('countryId', countryId);
    setField('regionId', '');
    setField('cities', []);
    setField('users', []);
  };

  const changeRegion = (regionId: string) => {
    setField('regionId', regionId);
    setField('cities', []);
    setField('users', []);
  };

  const togglePlatform = (platform: MobilePlatform) =>
    setField(
      'platforms',
      values.platforms.includes(platform)
        ? values.platforms.filter((item) => item !== platform)
        : [...values.platforms, platform],
      'push_mobile_platform',
    );

  return (
    <ModalDialog
      title={id ? 'Обновить уведомление' : 'Создать уведомление'}
      onClose={onClose}
      onSave={save}
      isSaving={isSaving}
    >
      <FormField label="Категория уведомления" errors={errors.category_id}>
        <FilterSelect
          options={categories}
          value={values.categoryId}
          isClearable={false}
          width={FIELD_WIDTH}
          onChange={(v) => setField('categoryId', v, 'category_id')}
        />
      </FormField>
      <FormField label="Тип уведомления" errors={errors.type_id}>
        <FilterSelect
          options={NOTIFICATION_TYPE_OPTIONS}
          value={values.typeId}
          isClearable={false}
          width={FIELD_WIDTH}
          onChange={(v) => setField('typeId', v, 'type_id')}
        />
      </FormField>
      <FormField label="Платформа" errors={errors.push_mobile_platform}>
        {PLATFORMS.map(({ value, label }) => (
          <label key={value} className="checkbox-inline">
            <input
              type="checkbox"
              checked={values.platforms.includes(value)}
              onChange={() => togglePlatform(value)}
            />
            {label}
          </label>
        ))}
      </FormField>
      <TextField
        label="Текст уведомления"
        multiline
        rows={6}
        placeholder="Введите текст уведомления"
        maxLength={MAX_MESSAGE_LENGTH}
        value={values.message}
        errors={errors.message}
        onChange={(v) => setField('message', v)}
      />
      <p className="text-muted" style={{ marginTop: -10 }}>{`${values.message.length}/${MAX_MESSAGE_LENGTH} знаков`}</p>
      <TextField
        label="Время отправки"
        type="datetime-local"
        value={values.sendTime}
        errors={errors.send_time}
        onChange={(v) => setField('sendTime', v, 'send_time')}
      />
      <FormField label="Страна">
        <FilterSelect
          options={COUNTRY_OPTIONS}
          value={values.countryId}
          isClearable={false}
          width={FIELD_WIDTH}
          onChange={changeCountry}
        />
      </FormField>
      <FormField label="Область">
        <FilterSelect
          options={regions.data ?? []}
          value={values.regionId}
          isClearable={false}
          isLoading={regions.isLoading}
          isDisabled={!values.countryId}
          width={FIELD_WIDTH}
          onChange={changeRegion}
        />
      </FormField>
      <FormField label="Город">
        <AsyncMultiSelect
          value={values.cities}
          width={FIELD_WIDTH}
          placeholder="Введите город"
          search={(query) => searchCities(values.countryId, values.regionId, query)}
          onChange={(v) => setField('cities', v)}
        />
      </FormField>
      <FormField label="Марка автомобиля">
        <AsyncMultiSelect
          value={values.marks}
          width={FIELD_WIDTH}
          placeholder="Введите марку"
          search={searchCarMarks}
          onChange={(v) => setField('marks', v)}
        />
      </FormField>
      {values.typeId === NOTIFICATION_TYPE.SELECTED_USERS && (
        <FormField label="Пользователи" errors={errors.user_ids}>
          <AsyncMultiSelect
            value={values.users}
            width={FIELD_WIDTH}
            placeholder="Введите пользователя"
            search={(query) => searchUsers(values.countryId, values.regionId, query)}
            onChange={(v) => setField('users', v, 'user_ids')}
          />
        </FormField>
      )}
      <ErrorList errors={{ common: errors.common ?? [], server: errors.server ?? [] }} />
    </ModalDialog>
  );
};
